import { useMemo } from 'react';

import type { CreateSupplierWithBranch } from '@dcc-erp/shared';

import { useCreateSupplier } from '../../lib/queries/suppliers';
import {
  SpreadsheetImportModal,
  type ImportResult,
  type ParsedUnit,
} from '../SpreadsheetImportModal';
import {
  parseSupplierImportGrid,
  SUPPLIER_IMPORT_FIELDS,
  type SupplierImportRow,
} from './supplierImport';

export function SupplierImportModal({
  companyId,
  existingTaxIds,
  onClose,
}: {
  companyId: string | undefined;
  existingTaxIds: Set<string>;
  onClose: () => void;
}) {
  const create = useCreateSupplier(companyId); // POST /suppliers (entity + first branch) from part 1
  const parse = useMemo(
    () => (grid: string[][]) => parseSupplierImportGrid(grid, existingTaxIds),
    [existingTaxIds],
  );

  // The shared modal hands `runImport` the broad `ParsedUnit<T>[]` (already filtered
  // to data!==null upstream); we re-narrow each unit and skip any null-data row so
  // the signature matches `SpreadsheetImportModalProps.runImport` under strict TS.
  // Bulk import only CREATES new suppliers. A taxId already in the master is
  // filtered out at parse time (marked as an error, never a valid unit), so the
  // run never attempts an update — the backend `createWithBranch` throws on an
  // existing taxId. `updated` is therefore structurally always 0 (the field is
  // still required by `ImportResult`); we don't advertise an update split.
  async function runImport(
    valid: ParsedUnit<SupplierImportRow>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    let created = 0;
    let failed = 0;
    const errors: { code: string; message: string }[] = [];
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (u?.data == null) {
        onProgress(i + 1, valid.length);
        continue;
      }
      try {
        // RECONCILED with the real `CreateSupplierWithBranch` (see SupplierFormModal):
        // currency/country/isActive are REQUIRED on BOTH entity + branch; the first
        // branch carries the entity name. companyId + both `code`s are scoped/auto-gen
        // server-side. The plan's slimmer body would not satisfy the shared schema.
        const body: CreateSupplierWithBranch = {
          code: u.data.code,
          nameTh: u.data.nameTh,
          ...(u.data.taxId ? { taxId: u.data.taxId } : {}),
          ...(u.data.terms ? { terms: u.data.terms } : {}),
          currency: 'THB',
          country: 'TH',
          isActive: true,
          branch: {
            nameTh: u.data.nameTh,
            branchCode: u.data.branchCode,
            currency: 'THB',
            country: 'TH',
            isActive: true,
          },
        };
        await create.mutateAsync(body);
        created++;
      } catch (e) {
        failed++;
        errors.push({ code: u.data.code, message: e instanceof Error ? e.message : 'ผิดพลาด' });
      }
      onProgress(i + 1, valid.length);
    }
    return { created, updated: 0, failed, errors };
  }

  return (
    <SpreadsheetImportModal<SupplierImportRow>
      title="นำเข้าผู้ขาย (Suppliers)"
      fields={SUPPLIER_IMPORT_FIELDS}
      templateColumns={['code', 'nameTh', 'taxId', 'branchCode', 'terms']}
      templateExamples={[['S-00001', 'บริษัท ตัวอย่าง จำกัด', '0105556091233', '00000', 'N30']]}
      templateFilename="suppliers_template.xlsx"
      sheetName="Suppliers"
      parse={parse}
      previewColumns={[
        { header: 'รหัส', render: (d) => d.code },
        { header: 'ชื่อ', render: (d) => d.nameTh },
        { header: 'เลขภาษี', render: (d) => d.taxId },
        { header: 'สาขา', width: 80, render: (d) => d.branchCode },
      ]}
      runImport={runImport}
      onClose={onClose}
    />
  );
}
